import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
    Plus,
    Search,
    Building2,
    Trash2,
    X,
    LayoutGrid,
    ChevronDown,
    ArrowUpDown,
    Loader2,
    Filter,
    Globe2,
} from "lucide-react";
import { Layout } from "@/components/Layout";
import { TourButton } from "@/components/TourButton";
import { useTour } from "@/hooks/useTour";
import { licitacionesTour } from "@/tours";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { listTenders, bulkDeleteTenders, updateTender } from "@/lib/api";
import { fmtEUR, fmtDate, daysUntil, statusLabel } from "@/lib/format";
import { COMMUNITIES, detectCommunity, communityLabel } from "@/lib/communities";
import { toast } from "sonner";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";

// Statuses the user can transition a tender into directly from the list.
const EDITABLE_STATUSES = [
    { value: "draft", label: "Borrador" },
    { value: "analyzed", label: "Analizada" },
    { value: "in_progress", label: "En preparación" },
    { value: "submitted", label: "Presentada" },
    { value: "archived", label: "Abandonada" },
];

const SORT_OPTIONS = [
    { value: "deadline_asc", label: "Plazo más próximo" },
    { value: "deadline_desc", label: "Plazo más lejano" },
    { value: "budget_desc", label: "Mayor presupuesto" },
    { value: "budget_asc", label: "Menor presupuesto" },
    { value: "created_desc", label: "Más recientes" },
    { value: "created_asc", label: "Más antiguas" },
    { value: "title_asc", label: "Título A→Z" },
];

// Status pill with chevron that opens an inline popover to change status.
function InlineStatus({ tender, onChange }) {
    const [open, setOpen] = useState(false);
    const [saving, setSaving] = useState(false);

    const select = async (nextStatus) => {
        if (nextStatus === tender.status) {
            setOpen(false);
            return;
        }
        setSaving(true);
        try {
            const updated = await updateTender(tender.id, { status: nextStatus });
            onChange?.(updated);
            toast.success(
                `«${tender.title.slice(0, 50)}» → ${statusLabel[nextStatus] || nextStatus}`,
            );
        } catch (e) {
            toast.error("No se pudo actualizar el estado");
        } finally {
            setSaving(false);
            setOpen(false);
        }
    };

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    onClick={(e) => e.stopPropagation()}
                    disabled={saving}
                    className="inline-flex items-center gap-1.5 rounded-full hover:ring-2 hover:ring-brand-200 transition-all"
                    data-testid={`status-trigger-${tender.id}`}
                >
                    <StatusBadge status={tender.status} />
                    {saving ? (
                        <Loader2 size={11} className="animate-spin text-ink-400" />
                    ) : (
                        <ChevronDown size={11} className="text-ink-400" />
                    )}
                </button>
            </PopoverTrigger>
            <PopoverContent
                className="p-1 w-52 rounded-xl"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-ink-400">
                    Cambiar estado
                </div>
                {EDITABLE_STATUSES.map((s) => (
                    <button
                        key={s.value}
                        type="button"
                        onClick={() => select(s.value)}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium hover:bg-brand-50 hover:text-brand-700 transition-colors flex items-center justify-between ${
                            s.value === tender.status ? "text-brand-700 bg-brand-50" : "text-ink-700"
                        }`}
                        data-testid={`status-option-${tender.id}-${s.value}`}
                    >
                        {s.label}
                        {s.value === tender.status && (
                            <span className="text-brand-600 text-xs font-bold">●</span>
                        )}
                    </button>
                ))}
            </PopoverContent>
        </Popover>
    );
}

export default function Licitaciones() {
    const tour = useTour("tour:licitaciones", licitacionesTour, { autoStart: true });
    const [tenders, setTenders] = useState([]);
    const [selected, setSelected] = useState(new Set());
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState("");
    const [filter, setFilter] = useState("all");
    const [communityFilter, setCommunityFilter] = useState("all");
    const [authorityFilter, setAuthorityFilter] = useState("all");
    const [sort, setSort] = useState("deadline_asc");
    const navigate = useNavigate();

    useEffect(() => {
        (async () => {
            try {
                setTenders(await listTenders());
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    // Detect community per tender (memoised).
    const tendersWithCommunity = useMemo(
        () =>
            tenders.map((t) => ({
                ...t,
                _community: detectCommunity(t)?.key || "",
            })),
        [tenders],
    );

    // Unique contracting authorities for the filter dropdown.
    const authorityOptions = useMemo(() => {
        const set = new Map();
        for (const t of tenders) {
            const a = (t.contracting_authority || "").trim();
            if (a) set.set(a, (set.get(a) || 0) + 1);
        }
        return Array.from(set.entries())
            .sort((a, b) => b[1] - a[1])
            .map(([a, n]) => ({ value: a, label: a, count: n }));
    }, [tenders]);

    // Unique communities present in the user's data (for the dropdown).
    const communityOptions = useMemo(() => {
        const present = new Set(
            tendersWithCommunity.map((t) => t._community).filter(Boolean),
        );
        return COMMUNITIES.filter((c) => present.has(c.key));
    }, [tendersWithCommunity]);

    const filtered = useMemo(() => {
        let arr = tendersWithCommunity;
        if (filter !== "all") arr = arr.filter((t) => t.status === filter);
        if (communityFilter !== "all")
            arr = arr.filter((t) => t._community === communityFilter);
        if (authorityFilter !== "all")
            arr = arr.filter(
                (t) =>
                    (t.contracting_authority || "").trim() === authorityFilter,
            );
        if (query) {
            const q = query.toLowerCase();
            arr = arr.filter(
                (t) =>
                    t.title.toLowerCase().includes(q) ||
                    (t.reference || "").toLowerCase().includes(q) ||
                    (t.contracting_authority || "").toLowerCase().includes(q),
            );
        }
        // Sort
        const next = [...arr];
        const cmpStr = (a, b) => (a || "").localeCompare(b || "");
        const cmpNum = (a, b) => (a || 0) - (b || 0);
        const cmpDate = (a, b) => {
            const av = a ? new Date(a).getTime() : Infinity;
            const bv = b ? new Date(b).getTime() : Infinity;
            return av - bv;
        };
        switch (sort) {
            case "deadline_asc":
                next.sort((a, b) => cmpDate(a.deadline, b.deadline));
                break;
            case "deadline_desc":
                next.sort((a, b) => cmpDate(b.deadline, a.deadline));
                break;
            case "budget_desc":
                next.sort((a, b) => cmpNum(b.budget, a.budget));
                break;
            case "budget_asc":
                next.sort((a, b) => cmpNum(a.budget, b.budget));
                break;
            case "created_desc":
                next.sort((a, b) => cmpDate(b.created_at, a.created_at));
                break;
            case "created_asc":
                next.sort((a, b) => cmpDate(a.created_at, b.created_at));
                break;
            case "title_asc":
                next.sort((a, b) => cmpStr(a.title, b.title));
                break;
            default:
                break;
        }
        return next;
    }, [tendersWithCommunity, filter, communityFilter, authorityFilter, query, sort]);

    const resetFilters = () => {
        setQuery("");
        setFilter("all");
        setCommunityFilter("all");
        setAuthorityFilter("all");
        setSort("deadline_asc");
    };

    const activeFiltersCount =
        (filter !== "all" ? 1 : 0) +
        (communityFilter !== "all" ? 1 : 0) +
        (authorityFilter !== "all" ? 1 : 0) +
        (query ? 1 : 0);

    const applyStatusChange = (updated) => {
        setTenders((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
    };

    return (
        <Layout>
            <motion.div
                className="w-full max-w-[1600px] mx-auto p-4 md:p-8"
                data-testid="licitaciones-page"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            >
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
                    <div>
                        <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-brand-600 mb-2 flex items-center gap-2">
                            <span className="inline-block w-6 h-px bg-brand-600" />
                            Licitaciones
                        </div>
                        <h1 className="font-display text-4xl md:text-5xl tracking-tight font-black text-ink-950 leading-[1.05]">
                            Cartera completa
                        </h1>
                        <p className="text-base text-ink-500 mt-2 font-medium">
                            {loading
                                ? "Cargando…"
                                : `${tenders.length} licitación${tenders.length === 1 ? "" : "es"} · ${filtered.length} visible${filtered.length === 1 ? "" : "s"}${
                                      activeFiltersCount
                                          ? ` · ${activeFiltersCount} filtro${activeFiltersCount > 1 ? "s" : ""} activo${activeFiltersCount > 1 ? "s" : ""}`
                                          : ""
                                  }`}
                        </p>
                    </div>
                    <Button
                        className="rounded-full bg-ink-950 text-white hover:bg-ink-800 h-12 px-7 font-bold shadow-pop-lg transition-all duration-300 hover:-translate-y-0.5 group"
                        onClick={() => navigate("/app/nueva")}
                        data-testid="licitaciones-new-tender-btn"
                    >
                        <Plus
                            size={16}
                            className="mr-2 transition-transform group-hover:rotate-90"
                        />
                        Nueva licitación
                    </Button>
                </div>

                {/* Search + filters */}
                <div className="bg-white border border-ink-100 rounded-2xl p-4 mb-5">
                    <div className="grid grid-cols-1 lg:grid-cols-[1fr,auto,auto,auto] gap-3 items-center">
                        <div className="relative">
                            <Search
                                size={15}
                                className="absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-400"
                            />
                            <Input
                                placeholder="Buscar por título, referencia u órgano…"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                className="pl-10 rounded-xl border-ink-100 h-11 bg-white font-medium"
                                data-testid="licitaciones-search"
                            />
                        </div>

                        {/* Community filter */}
                        <Select value={communityFilter} onValueChange={setCommunityFilter}>
                            <SelectTrigger
                                className="rounded-xl border-ink-100 h-11 min-w-[180px]"
                                data-testid="filter-community"
                            >
                                <span className="inline-flex items-center gap-1.5">
                                    <Globe2 size={13} className="text-ink-400" />
                                    <SelectValue placeholder="Comunidad" />
                                </span>
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Todas las comunidades</SelectItem>
                                {communityOptions.map((c) => (
                                    <SelectItem key={c.key} value={c.key}>
                                        {c.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>

                        {/* Contracting authority */}
                        <Select value={authorityFilter} onValueChange={setAuthorityFilter}>
                            <SelectTrigger
                                className="rounded-xl border-ink-100 h-11 min-w-[200px] max-w-[280px]"
                                data-testid="filter-authority"
                            >
                                <span className="inline-flex items-center gap-1.5 truncate">
                                    <Building2 size={13} className="text-ink-400 shrink-0" />
                                    <SelectValue placeholder="Órgano" />
                                </span>
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Todos los órganos</SelectItem>
                                {authorityOptions.map((a) => (
                                    <SelectItem key={a.value} value={a.value}>
                                        <span className="truncate">{a.label}</span>{" "}
                                        <span className="text-ink-400 text-xs">({a.count})</span>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>

                        {/* Sort */}
                        <Select value={sort} onValueChange={setSort}>
                            <SelectTrigger
                                className="rounded-xl border-ink-100 h-11 min-w-[200px]"
                                data-testid="sort-select"
                            >
                                <span className="inline-flex items-center gap-1.5">
                                    <ArrowUpDown size={13} className="text-ink-400" />
                                    <SelectValue placeholder="Ordenar" />
                                </span>
                            </SelectTrigger>
                            <SelectContent>
                                {SORT_OPTIONS.map((o) => (
                                    <SelectItem key={o.value} value={o.value}>
                                        {o.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Status pill bar */}
                    <div className="flex items-center gap-3 mt-3 flex-wrap">
                        <Filter size={13} className="text-ink-400 shrink-0" />
                        <div className="flex gap-1 bg-ink-50 border border-ink-100 p-1 rounded-xl overflow-x-auto">
                            {["all", "draft", "analyzed", "in_progress", "submitted", "archived"].map(
                                (s) => (
                                    <button
                                        key={s}
                                        onClick={() => setFilter(s)}
                                        className={`px-3.5 h-8 text-xs font-bold rounded-lg transition-all whitespace-nowrap ${
                                            filter === s
                                                ? "bg-ink-950 text-white shadow-pop"
                                                : "text-ink-500 hover:bg-white hover:text-brand-600"
                                        }`}
                                        data-testid={`licitaciones-filter-${s}`}
                                    >
                                        {s === "all"
                                            ? "Todas"
                                            : s === "archived"
                                              ? "Abandonadas"
                                              : statusLabel[s]}
                                    </button>
                                ),
                            )}
                        </div>
                        {activeFiltersCount > 0 && (
                            <button
                                type="button"
                                onClick={resetFilters}
                                className="ml-auto text-xs font-bold text-ink-500 hover:text-red-600 inline-flex items-center gap-1 rounded-lg px-2 py-1 hover:bg-red-50 transition-colors"
                                data-testid="clear-filters"
                            >
                                <X size={12} />
                                Limpiar filtros
                            </button>
                        )}
                    </div>
                </div>

                {/* Bulk actions */}
                {selected.size > 0 && (
                    <div
                        className="flex items-center justify-between gap-3 mb-4 bg-ink-950 text-white rounded-xl px-5 py-3 shadow-pop"
                        data-testid="bulk-actions-bar"
                    >
                        <div className="flex items-center gap-3">
                            <button
                                onClick={() => setSelected(new Set())}
                                className="w-7 h-7 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center transition-colors"
                                data-testid="bulk-clear-btn"
                            >
                                <X size={14} />
                            </button>
                            <span className="font-bold text-sm">
                                {selected.size} licitación{selected.size > 1 ? "es" : ""}{" "}
                                seleccionada{selected.size > 1 ? "s" : ""}
                            </span>
                        </div>
                        <Button
                            onClick={() => setConfirmDelete(true)}
                            className="rounded-full bg-white text-red-600 hover:bg-red-50 h-9 px-4 font-bold"
                            data-testid="bulk-delete-btn"
                        >
                            <Trash2 size={14} className="mr-1.5" />
                            Eliminar seleccionadas
                        </Button>
                    </div>
                )}

                {/* Table */}
                <div className="bg-white border border-ink-100 rounded-2xl shadow-pop overflow-hidden">
                    {/* Sort indicator banner */}
                    {!loading && filtered.length > 0 && (
                        <div
                            className="px-5 py-2.5 bg-slate-50 border-b border-slate-100 flex items-center justify-between gap-3 text-xs font-medium"
                            data-testid="sort-banner"
                        >
                            <div className="flex items-center gap-2 text-ink-500">
                                <ArrowUpDown size={11} />
                                Ordenado por:{" "}
                                <span className="text-ink-900 font-bold">
                                    {SORT_OPTIONS.find((o) => o.value === sort)?.label}
                                </span>
                            </div>
                            <div className="text-ink-400">
                                {filtered.length} resultado{filtered.length === 1 ? "" : "s"}
                            </div>
                        </div>
                    )}

                    {loading ? (
                        <div className="p-6 space-y-3">
                            {[0, 1, 2, 3, 4].map((i) => (
                                <Skeleton key={i} className="h-12 w-full rounded-xl" />
                            ))}
                        </div>
                    ) : filtered.length === 0 ? (
                        <EmptyState
                            hasTenders={tenders.length > 0}
                            onCreate={() => navigate("/app/nueva")}
                        />
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full data-table">
                                <thead>
                                    <tr>
                                        <th className="w-10">
                                            <Checkbox
                                                checked={
                                                    filtered.length > 0 &&
                                                    filtered.every((t) => selected.has(t.id))
                                                }
                                                onCheckedChange={(v) => {
                                                    if (v) {
                                                        setSelected(
                                                            new Set(filtered.map((t) => t.id)),
                                                        );
                                                    } else {
                                                        setSelected(new Set());
                                                    }
                                                }}
                                                data-testid="select-all-checkbox"
                                            />
                                        </th>
                                        <th>Licitación</th>
                                        <th>Órgano</th>
                                        <th>Comunidad</th>
                                        <th>Estado</th>
                                        <th className="text-right">Presupuesto</th>
                                        <th>Plazo</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filtered.map((t) => {
                                        const d = daysUntil(t.deadline);
                                        const isSel = selected.has(t.id);
                                        return (
                                            <tr
                                                key={t.id}
                                                className={`group cursor-pointer transition-colors ${
                                                    isSel ? "bg-brand-50" : "hover:bg-slate-50"
                                                }`}
                                                onClick={(e) => {
                                                    if (
                                                        e.target.closest(
                                                            "[data-testid^=row-checkbox]",
                                                        ) ||
                                                        e.target.closest(
                                                            "[data-testid^=status-trigger]",
                                                        )
                                                    )
                                                        return;
                                                    navigate(`/app/licitacion/${t.id}`);
                                                }}
                                                data-testid={`tender-row-${t.id}`}
                                            >
                                                <td
                                                    onClick={(e) => e.stopPropagation()}
                                                    data-testid={`row-checkbox-${t.id}`}
                                                >
                                                    <Checkbox
                                                        checked={isSel}
                                                        onCheckedChange={(v) => {
                                                            const next = new Set(selected);
                                                            if (v) next.add(t.id);
                                                            else next.delete(t.id);
                                                            setSelected(next);
                                                        }}
                                                    />
                                                </td>
                                                <td>
                                                    <div className="font-medium text-slate-900 group-hover:text-brand-600 transition-colors">
                                                        {t.title}
                                                    </div>
                                                    {t.reference && (
                                                        <div className="font-mono text-xs text-slate-500 mt-0.5">
                                                            {t.reference}
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="text-slate-600 text-sm">
                                                    <div className="flex items-center gap-1.5 max-w-[260px]">
                                                        <Building2
                                                            size={13}
                                                            className="text-slate-400 shrink-0"
                                                        />
                                                        <span className="truncate">
                                                            {t.contracting_authority || "—"}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td>
                                                    {t._community ? (
                                                        <span
                                                            className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-1 rounded-md border border-indigo-200 bg-indigo-50 text-indigo-700"
                                                            data-testid={`row-community-${t.id}`}
                                                        >
                                                            <Globe2 size={10} />
                                                            {communityLabel(t._community)}
                                                        </span>
                                                    ) : (
                                                        <span className="text-ink-300 text-xs">—</span>
                                                    )}
                                                </td>
                                                <td>
                                                    <InlineStatus
                                                        tender={t}
                                                        onChange={applyStatusChange}
                                                    />
                                                </td>
                                                <td className="num">{fmtEUR(t.budget)}</td>
                                                <td className="text-sm">
                                                    {t.deadline ? (
                                                        <div>
                                                            <div>{fmtDate(t.deadline)}</div>
                                                            {d !== null && d >= 0 && (
                                                                <div
                                                                    className={`text-xs mt-0.5 ${
                                                                        d < 7
                                                                            ? "text-red-600 font-medium"
                                                                            : d < 30
                                                                              ? "text-amber-700"
                                                                              : "text-slate-500"
                                                                    }`}
                                                                >
                                                                    {d === 0 ? "Hoy" : `${d} días`}
                                                                </div>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <span className="text-slate-400">—</span>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </motion.div>

            <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
                <AlertDialogContent className="rounded-2xl">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="font-display">
                            ¿Eliminar {selected.size} licitación
                            {selected.size > 1 ? "es" : ""}?
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            Esta acción no se puede deshacer. Se borrarán los pliegos,
                            análisis y anexos asociados.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-xl">Cancelar</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={async () => {
                                setDeleting(true);
                                try {
                                    const ids = Array.from(selected);
                                    await bulkDeleteTenders(ids);
                                    setTenders((prev) =>
                                        prev.filter((t) => !selected.has(t.id)),
                                    );
                                    setSelected(new Set());
                                    toast.success(
                                        `${ids.length} licitación${
                                            ids.length > 1 ? "es eliminadas" : " eliminada"
                                        }`,
                                    );
                                } catch {
                                    toast.error("No se pudieron eliminar");
                                } finally {
                                    setDeleting(false);
                                    setConfirmDelete(false);
                                }
                            }}
                            disabled={deleting}
                            className="rounded-xl bg-red-600 hover:bg-red-700"
                            data-testid="confirm-bulk-delete"
                        >
                            {deleting ? "Eliminando…" : "Sí, eliminar"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
            <TourButton onClick={tour.start} label="Ver tour" />
        </Layout>
    );
}

const EmptyState = ({ hasTenders, onCreate }) => (
    <div className="py-20 text-center px-6" data-testid="empty-state">
        <div className="w-16 h-16 mx-auto bg-gradient-to-br from-brand-600 to-cyan-500 text-white flex items-center justify-center rounded-2xl mb-5 shadow-pop">
            <LayoutGrid size={26} />
        </div>
        <div className="font-display text-2xl font-black mb-2">
            {hasTenders ? "Sin resultados" : "Tu cartera está vacía"}
        </div>
        <div className="text-base text-ink-500 mb-7 max-w-md mx-auto font-medium">
            {hasTenders
                ? "Ajusta los filtros o la búsqueda."
                : "Sube tu primer pliego en PDF y deja que la IA trabaje: resumen, escandallo y anexos en menos de 3 minutos."}
        </div>
        {!hasTenders && (
            <Button
                onClick={onCreate}
                className="rounded-full bg-ink-950 hover:bg-ink-800 text-white h-12 px-7 font-bold shadow-pop-lg transition-all hover:-translate-y-0.5"
                data-testid="empty-state-cta"
            >
                <Plus size={16} className="mr-1.5" />
                Nueva licitación
            </Button>
        )}
    </div>
);
